import { request } from '../client';
import { endpoints } from '../endpoints';
import { asList } from './shape';

/** Normalise a Spring `Page` (content/totalElements/totalPages) or a plain array. */
const toPage = (raw, page, pageSize) => {
  if (Array.isArray(raw)) return { items: raw, total: raw.length, totalPages: 1, page, pageSize };
  const items = (raw && raw.content) || [];
  const total = raw && typeof raw.totalElements === 'number' ? raw.totalElements : items.length;
  const totalPages = raw && typeof raw.totalPages === 'number' ? raw.totalPages : Math.max(1, Math.ceil(total / pageSize));
  return { items, total, totalPages, page, pageSize };
};

/** status: 'active' | 'draft' | 'closed' (values as stored by the current backend); page is 1-based. */
export const listProjectsByStatus = async (status, page = 1, pageSize = 10, opts) =>
  toPage(await request(endpoints.projects.byStatus(status, page, pageSize), opts), page, pageSize);

// The handover warns this is slow on real data (a few seconds), so allow more time and prefer the paged call.
export const listAllProjects = async (opts) => asList(await request(endpoints.projects.all(), { timeoutMs: 45000, ...opts }));

/** Resolves to the project, or null when not found (current API returns 200 + empty body). */
export const getProject = (id, opts) => request(endpoints.projects.byId(id), opts);

export const createProject = (payload) => request(endpoints.projects.create(), { method: 'POST', body: payload });

export const updateProject = (id, patch) => request(endpoints.projects.update(id), { method: 'PATCH', body: patch });

const idOf = (x) => (x && typeof x === 'object' ? x.id : x);

/**
 * Set a project's category and target-group links.
 *
 * CURRENT-BACKEND QUIRK: PATCH /project/update/{id} overwrites BOTH lists from the body
 * (a missing list is saved as null). So this always sends the full current lists, merged
 * with the changes, using id strings (as the legacy UI did; Spring resolves them to refs).
 * ASSUMPTION: the Express backend may offer a safer partial update; adapt here only.
 */
export const setProjectLinks = (project, { categoryIds, targetGroupIds } = {}) =>
  updateProject(project.id, {
    projectCategoryId: categoryIds || (project.projectCategoryId || []).map(idOf),
    targetGroups: targetGroupIds || (project.targetGroups || []).map(idOf),
    targetGrouplist: project.targetGrouplist || [],
  });

/**
 * Change a project's contact reference and/or settings (and optionally its status) through the update endpoint.
 * Per the backend, that endpoint ALWAYS replaces the three link lists, so they are sent unchanged; the other
 * fields change only when sent, and `projectSettings` is merged into the existing settings on the server.
 * @param {{ id: string, projectCategoryId?: any[], targetGroups?: any[], targetGrouplist?: any[] }} project
 * @param {{ projectReference?: object, projectSettings?: object, projectStatus?: string, surveyUrl?: string | null }} [details]
 */
export const saveProjectDetails = (project, { projectReference, projectSettings, projectStatus, surveyUrl } = {}) =>
  updateProject(project.id, {
    projectCategoryId: (project.projectCategoryId || []).map(idOf),
    targetGroups: (project.targetGroups || []).map(idOf),
    targetGrouplist: project.targetGrouplist || [],
    ...(projectReference !== undefined ? { projectReference } : {}),
    ...(projectSettings !== undefined ? { projectSettings } : {}),
    ...(projectStatus !== undefined ? { projectStatus } : {}),
    ...(surveyUrl !== undefined ? { surveyUrl } : {}),
  });

/** status: 'active' | 'draft' | 'closed'. Any case is accepted; the server returns it lower-case. */
export const setProjectStatus = (id, projectStatus) =>
  request(endpoints.projects.status(id), { method: 'PATCH', body: { projectStatus } });

/**
 * Field statistics for a project, from its redirect links (project level only; no per-quota-cell numbers yet):
 * { projectId, complete, screenout, quotaFull, total, wantedCompletes, remainingCompletes, progress, incidenceRate,
 * quotaFullRate }. Rates are percentages and null when there is nothing to divide by. 404 (empty body) -> null.
 */
export const getProjectStatistics = (id, opts) => request(endpoints.projects.statistics(id), opts);

/**
 * Is the campaign ready to launch? { ready, problems: string[], warnings: string[] }. The server runs the same check
 * when the status is changed to active (400 "Campaign is not ready to launch" with the missing items in `errors`).
 * 404 (empty body) -> null.
 */
export const getProjectReadiness = (id, opts) => request(endpoints.projects.readiness(id), opts);

/**
 * Estimated cost per target group: rate x wanted completes, the rate coming from the rate card for the group's IR and
 * LOI. The rate's unit is NOT confirmed by the backend: show `basis.rateUnit` (and `basis.currency` when set), never
 * assume a currency. Null figures come with their reasons in `notEnoughData`. 404 (empty body) -> null.
 */
export const getProjectPricing = (id, opts) => request(endpoints.projects.pricing(id), opts);

/** Respondent redirect counts for a project: { complete, screenout, quotaFull, total }. */
export const getRedirectStats = (id, opts) => request(endpoints.projects.redirectStats(id), opts);

export const deleteProject =(id) => request(endpoints.projects.remove(id), { method: 'DELETE' });
